import React, { useEffect, useState, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Box, CircularProgress, Typography, Alert } from '@mui/material';

// Cognito Configuration from Environment Variables
const COGNITO_CONFIG = {
  userPoolId: process.env.REACT_APP_COGNITO_USER_POOL_ID || '',
  userPoolClientId: process.env.REACT_APP_COGNITO_CLIENT_ID || '',
  cognitoDomain: process.env.REACT_APP_COGNITO_DOMAIN || '',
  redirectSignIn: process.env.REACT_APP_COGNITO_REDIRECT_SIGN_IN || 'http://localhost:3000/callback',
  redirectSignOut: process.env.REACT_APP_COGNITO_REDIRECT_SIGN_OUT || 'http://localhost:3000'
};

interface TokenResponse {
  access_token: string;
  id_token: string;
  refresh_token: string;
  expires_in: number;
  token_type: string;
}

interface UserInfo {
  sub: string;
  email: string;
  email_verified: string;
  'custom:tenant_id'?: string;
  username?: string;
}

export const Callback: React.FC = () => {
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [debugInfo, setDebugInfo] = useState<string>('');
  const [isProcessing, setIsProcessing] = useState(false);
  const hasProcessedRef = useRef(false);
  const navigate = useNavigate();
  const location = useLocation();

  const exchangeCodeForTokens = async (code: string): Promise<TokenResponse> => {
    const tokenEndpoint = `https://${COGNITO_CONFIG.cognitoDomain}/oauth2/token`;
    
    const params = new URLSearchParams({
      grant_type: 'authorization_code',
      client_id: COGNITO_CONFIG.userPoolClientId,
      code: code,
      redirect_uri: COGNITO_CONFIG.redirectSignIn
    });

    const response = await fetch(tokenEndpoint, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded',
      },
      body: params
    });

    if (!response.ok) {
      const errorText = await response.text();
      console.error('Token exchange failed:', { status: response.status, error: errorText });
      throw new Error(`Token exchange failed: ${response.status} ${errorText}`);
    }

    return await response.json();
  };

  const getUserInfo = async (accessToken: string): Promise<UserInfo> => {
    const userInfoEndpoint = `https://${COGNITO_CONFIG.cognitoDomain}/oauth2/userInfo`;

    const response = await fetch(userInfoEndpoint, {
      headers: {
        'Authorization': `Bearer ${accessToken}`
      }
    });

    if (!response.ok) {
      throw new Error(`Failed to get user info: ${response.status}`);
    }

    return await response.json();
  };

  useEffect(() => {
    const handleCallback = async () => {
      // Prevent multiple simultaneous processing
      if (isProcessing || hasProcessedRef.current) {
        setDebugInfo('Already processing authentication or already processed...');
        return;
      }

      try {
        setIsProcessing(true);
        hasProcessedRef.current = true;
        setDebugInfo('Starting OAuth callback processing...');
        
        // Check if there's an authorization code in the URL
        const urlParams = new URLSearchParams(location.search);
        const code = urlParams.get('code');
        const error = urlParams.get('error');

        if (error) {
          throw new Error(`Authentication error: ${error}`);
        }

        if (!code) {
          throw new Error('No authorization code received');
        }

        // Check if we've already processed this code to prevent duplicate requests
        const processedCode = sessionStorage.getItem('processed_auth_code');
        if (processedCode === code) {
          setDebugInfo('Authorization code already processed, redirecting...');
          window.location.href = process.env.REACT_APP_COGNITO_REDIRECT_SIGN_OUT || 'http://localhost:3000';
          return;
        }

        // Check if this code is currently being processed by another instance
        const processingCode = sessionStorage.getItem('processing_auth_code');
        if (processingCode === code) {
          setDebugInfo('Authorization code is being processed by another instance...');
          return;
        }

        // Immediately mark this code as being processed to prevent race conditions
        sessionStorage.setItem('processing_auth_code', code);

        setDebugInfo(`Authorization code received: ${code.substring(0, 10)}...`);

        // Exchange authorization code for tokens
        setDebugInfo('Exchanging authorization code for tokens...');
        // Removed console.log to prevent token exposure
        const tokens = await exchangeCodeForTokens(code);

        // Get user information using the access token
        setDebugInfo('Fetching user information...');
        const userInfo = await getUserInfo(tokens.access_token);

        // Store tokens and user info
        localStorage.setItem('access_token', tokens.access_token);
        localStorage.setItem('id_token', tokens.id_token);
        localStorage.setItem('refresh_token', tokens.refresh_token);
        localStorage.setItem('token_expires_at', (Date.now() + tokens.expires_in * 1000).toString());
        localStorage.setItem('user_info', JSON.stringify(userInfo));
        
        // Mark this code as processed to prevent duplicate requests
        sessionStorage.setItem('processed_auth_code', code);
        sessionStorage.removeItem('processing_auth_code');
        
        setDebugInfo('Authentication successful, redirecting to main application...');
        
        // Clean up the URL to remove the authorization code
        window.history.replaceState({}, document.title, window.location.pathname);
        
        // Redirect to main app
        window.location.href = process.env.REACT_APP_COGNITO_REDIRECT_SIGN_OUT || 'http://localhost:3000';
        
      } catch (error: any) {
        // Clean up processing flag on error
        sessionStorage.removeItem('processing_auth_code');
        setError(error.message || 'Authentication failed');
        setDebugInfo(`Error: ${error.message}`);
        setIsLoading(false);
      } finally {
        setIsProcessing(false);
      }
    };

    handleCallback();

    // Cleanup function to handle component unmounting
    return () => {
      // If component unmounts while processing, clean up the processing flag
      const processingCode = sessionStorage.getItem('processing_auth_code');
      if (processingCode) {
        sessionStorage.removeItem('processing_auth_code');
      }
    };
  }, [navigate, location.search, isProcessing]); // Use location.search instead of location to be more specific

  if (error) {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '100vh',
          gap: 2,
        }}
      >
        <Alert severity="error" sx={{ maxWidth: 400 }}>
          {error}
        </Alert>
        <Typography variant="body2" color="text.secondary">
          Debug: {debugInfo}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Redirecting to login...
        </Typography>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
        minHeight: '100vh',
        gap: 2,
      }}
    >
      <CircularProgress />
      <Typography variant="body2" color="text.secondary">
        {debugInfo || 'Completing authentication...'}
      </Typography>
    </Box>
  );
};
